/**
 * ContractDetail — Page complète d'un contrat : infos, actions (reconduire,
 * préavis, résiliation), dossiers/documents GED, factures liées.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, RefreshCw, Send, Ban, FolderPlus, FileText, Upload,
  Trash2, Building2, User, CheckCircle2, Calendar, FileSignature, Undo2,
  Pencil, MoveRight, FolderCog, Car, Link2, Search, Calculator, Sparkles,
  ChevronRight,
} from "lucide-react";
import { ZipImportButton } from "@/components/contracts/ZipImportButton";
import AnalyzeSourceDialog from "@/components/contracts/AnalyzeSourceDialog";
import { toast } from "sonner";
import ResiliationDialog from "@/components/ResiliationDialog";
import ContractAiPanel from "@/components/ContractAiPanel";
import ContractBillingBridgePanel from "@/components/ContractBillingBridgePanel";
import TerminationSimulatorDialog from "@/components/TerminationSimulatorDialog";
import LetterAssistantDialog from "@/components/LetterAssistantDialog";
import ClauseNegotiatorDialog from "@/components/ClauseNegotiatorDialog";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";
const CONTRACT_DOC_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.msg,.eml,.zip";

const CYCLE_FR = {
  one_shot: "Ponctuel", ponctuel: "Ponctuel",
  monthly: "Mensuel", mensuel: "Mensuel",
  quarterly: "Trimestriel", trimestriel: "Trimestriel",
  semi_annual: "Semestriel", semestriel: "Semestriel",
  annual: "Annuel", annuel: "Annuel", yearly: "Annuel",
};
const CONTRACT_TYPE_FR = {
  bail_commercial: "Bail commercial", bail_habitation: "Bail habitation",
  prestation_saas: "Prestation SaaS", prestation_service: "Prestation service",
  cdi: "CDI", cdd: "CDD", freelance: "Freelance", nda: "NDA",
  contrat_cadre: "Contrat cadre", assurance: "Assurance",
  location_vehicule: "Location véhicule", maintenance: "Maintenance",
  sponsoring: "Sponsoring", distribution: "Distribution", autre: "Autre",
};

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("contracts.write");
  const [contract, setContract] = useState(null);
  const [showReconduct, setShowReconduct] = useState(false);
  const [showPreavis, setShowPreavis] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [openFolders, setOpenFolders] = useState({});
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const suggestAnalyze = () => {
    if (contract?.ai_extracted_at || !hasPermission("contracts.write")) return;
    toast("Ce contrat n'a pas encore d'analyse IA", {
      description: "Analyser le PDF pour compléter les champs, détecter les risques et les obligations ?",
      action: { label: "Analyser maintenant", onClick: () => setAnalyzeOpen(true) },
      duration: 12000,
    });
  };
  const toggleFolder = (fid) => setOpenFolders((o) => ({ ...o, [fid]: !o[fid] }));

  const [showResiliation, setShowResiliation] = useState(false);
  const [resiliationDocs, setResiliationDocs] = useState({ documents: [], resiliation_status: "none" });
  const [reconductNote, setReconductNote] = useState("");
  const [preavisNote, setPreavisNote] = useState("");
  const [cancelDate, setCancelDate] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [folderName, setFolderName] = useState("");
  const [linkedVehicle, setLinkedVehicle] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [vehicleCandidates, setVehicleCandidates] = useState([]);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [showTermSim, setShowTermSim] = useState(false);
  const [showLetterAssist, setShowLetterAssist] = useState(false);

  const loadVehicleLink = async () => {
    try {
      const r = await api.get(`/contracts/${id}/vehicle-link`);
      setLinkedVehicle(r.data);
    } catch { /* silent — endpoint may be unavailable for non-fleet plans */ }
  };
  const searchVehicles = async () => {
    try {
      const r = await api.get("/vehicle-candidates", { params: vehicleSearch ? { q: vehicleSearch } : {} });
      setVehicleCandidates(r.data.vehicles || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  const linkVehicle = async (vid) => {
    try {
      await api.post(`/contracts/${id}/link-vehicle`, { vehicle_id: vid });
      toast.success("Véhicule lié"); setLinkDialogOpen(false); loadVehicleLink();
    } catch (e) { toast.error(apiError(e)); }
  };
  const unlinkVehicle = async () => {
    if (!window.confirm("Retirer le lien avec ce véhicule ?")) return;
    try { await api.delete(`/contracts/${id}/unlink-vehicle`); toast.success("Véhicule délié"); loadVehicleLink(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const load = async () => {
    try {
      const [rc, rr] = await Promise.all([
        api.get(`/contracts/${id}`),
        api.get(`/contracts/${id}/demande-resiliation/documents`),
      ]);
      setContract(rc.data.contract);
      setResiliationDocs(rr.data);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/app/contracts");
    }
  };
  const annulerResiliation = async () => {
    if (!window.confirm("Annuler la demande de résiliation en cours ? Le contrat redevient actif, les documents sont conservés pour l'historique.")) return;
    try {
      await api.post(`/contracts/${id}/demande-resiliation/annuler`);
      toast.success("Résiliation annulée — contrat redevenu actif.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); if (contract?.party_type !== "client") loadVehicleLink(); /* eslint-disable-next-line */ }, [id]);

  if (!contract) {
    return <div className="p-8 text-sm text-slate-500">Chargement…</div>;
  }

  const renew = async () => {
    if (!window.confirm(`Reconduire de ${contract.tacit_renewal_period_months} mois ?`)) return;
    try {
      const r = await api.post(`/contracts/${id}/renew`);
      toast.success(`Reconduit jusqu'au ${fmtDate(r.data.new_end_date)}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const markReconduct = async () => {
    try {
      await api.post(`/contracts/${id}/mark-reconduction`, { note: reconductNote });
      toast.success("Reconduction enregistrée.");
      setShowReconduct(false); setReconductNote("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const markPreavis = async () => {
    try {
      await api.post(`/contracts/${id}/preavis-sent`, { note: preavisNote });
      toast.success("Préavis marqué comme envoyé.");
      setShowPreavis(false); setPreavisNote("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const scheduleCancel = async () => {
    if (!cancelDate) { toast.error("Date effective requise."); return; }
    try {
      await api.post(`/contracts/${id}/cancel-request`, {
        effective_date: cancelDate, reason: cancelReason,
      });
      toast.success("Résiliation programmée.");
      setShowCancel(false); setCancelDate(""); setCancelReason("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const createFolder = async () => {
    if (!folderName.trim()) return;
    try {
      await api.post(`/contracts/${id}/folders`, { name: folderName.trim() });
      toast.success("Dossier créé.");
      setShowNewFolder(false); setFolderName("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteFolder = async (fid) => {
    if (!window.confirm("Supprimer ce dossier et ses documents ?")) return;
    try {
      await api.delete(`/contracts/${id}/folders/${fid}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const uploadDocs = async (folderId, fileList) => {
    setOpenFolders((o) => ({ ...o, [folderId]: true }));
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const tid = toast.loading(`Envoi 0/${files.length}…`);
    let ok = 0;
    for (const [i, file] of files.entries()) {
      try {
        const fd = new FormData();
        fd.append("folder_id", folderId); fd.append("file", file);
        await api.post(`/contracts/${id}/documents/upload`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) { toast.error(`${file.name} : ${apiError(e)}`); }
      toast.loading(`Envoi ${i + 1}/${files.length}…`, { id: tid });
    }
    toast.success(`${ok}/${files.length} document(s) ajouté(s).`, { id: tid });
    if (ok) suggestAnalyze();
    load();
  };
  const detachDoc = async (did) => {
    if (!window.confirm("Retirer ce document ?")) return;
    try {
      await api.delete(`/contracts/${id}/documents/${did}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const renameFolder = async (fid, currentName) => {
    const newName = window.prompt("Nouveau nom du dossier :", currentName);
    if (!newName || newName === currentName) return;
    try {
      await api.put(`/contracts/${id}/folders/${fid}`, { name: newName });
      toast.success("Dossier renommé.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const moveDoc = async (did) => {
    const folders = contract.folders || [];
    if (!folders.length) return toast.error("Aucun dossier disponible");
    const opts = folders.map((f, i) => `${i + 1}. ${f.name}`).join("\n");
    const idx = window.prompt(`Choisir le dossier cible (numéro) :\n${opts}\n\n0 = racine (sans dossier)`);
    if (idx == null) return;
    const n = parseInt(idx, 10);
    const target = n === 0 ? null : folders[n - 1]?.id;
    if (n > 0 && !target) return toast.error("Index invalide");
    try {
      await api.put(`/contracts/${id}/documents/${did}/move`, { target_folder_id: target });
      toast.success("Document déplacé.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="contract-detail-page">
      <button className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              onClick={() => navigate("/app/contracts/portefeuille")}
              data-testid="detail-back-btn">
        <ArrowLeft className="h-3.5 w-3.5" /> Retour au portefeuille
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            {contract.party_type === "client"
              ? <User className="h-4 w-4 text-emerald-500" />
              : <Building2 className="h-4 w-4 text-amber-500" />}
            <span className="text-xs text-slate-500">{contract.party_name}</span>
          </div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-semibold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            {contract.title}
          </h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-[10px] uppercase">
              {({active:"Actif",draft:"Brouillon",pending:"En attente",ended:"Terminé",cancelled:"Résilié",renewed:"Reconduit",paused:"En pause"}[contract.status]) || contract.status}
            </Badge>
            {contract.reference && (
              <span className="text-xs font-mono text-slate-400">{contract.reference}</span>
            )}
            {contract.cancellation_requested_at && (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]"
                     data-testid="detail-cancel-scheduled-badge">
                Résiliation prévue le {fmtDate(contract.cancellation_effective_date)}
              </Badge>
            )}
            {contract.preavis_sent_at && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]"
                     data-testid="detail-preavis-badge">
                Préavis envoyé le {fmtDate(contract.preavis_sent_at)}
              </Badge>
            )}
            {contract.ai_extracted_at && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">
                Analysé par IA
              </Badge>
            )}
          </div>
        </div>
        {canWrite && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowTermSim(true)}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid="detail-simulate-termination-btn">
              <Calculator className="h-4 w-4 mr-1.5" /> Si je résilie aujourd'hui ?
            </Button>
            <Button variant="outline" onClick={() => setShowLetterAssist(true)}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="detail-letter-assist-btn">
              <Sparkles className="h-4 w-4 mr-1.5" /> Rédiger une lettre
            </Button>
            {contract.end_date && (
              <Button variant="outline" onClick={renew} data-testid="detail-renew-btn">
                <RefreshCw className="h-4 w-4 mr-1.5" /> Reconduire
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowReconduct(true)}
                    data-testid="detail-mark-reconduct-btn">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Marquer reconduit
            </Button>
            <Button variant="outline" onClick={() => setShowPreavis(true)}
                    data-testid="detail-preavis-btn">
              <Send className="h-4 w-4 mr-1.5" /> Préavis envoyé
            </Button>
            <Button variant="outline" onClick={() => setShowCancel(true)}
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="detail-cancel-btn">
              <Ban className="h-4 w-4 mr-1.5" /> Résilier (rapide)
            </Button>
            {resiliationDocs.resiliation_status !== "en_cours" ? (
              <Button variant="outline" onClick={() => setShowResiliation(true)}
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      data-testid="detail-resiliation-workflow-btn">
                <FileSignature className="h-4 w-4 mr-1.5" /> Processus de résiliation
              </Button>
            ) : (
              <Button variant="outline" onClick={annulerResiliation}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="detail-resiliation-annuler-btn">
                <Undo2 className="h-4 w-4 mr-1.5" /> Annuler résiliation
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Panel IA — TL;DR + Risques + Timeline (Phase 1 game-changer) */}
      <ContractAiPanel contract={contract} onRefresh={load} canWrite={canWrite}
        onAnalyze={() => setAnalyzeOpen(true)} />
      <AnalyzeSourceDialog contractId={id} open={analyzeOpen} onClose={() => setAnalyzeOpen(false)}
        onDone={() => { setAnalyzeOpen(false); load(); }} />

      {/* PDF source du contrat — visible inline si extrait par IA */}
      {contract.file_asset_id && (
        <Card data-testid="contract-pdf-preview-card">
          <CardContent className="p-0 overflow-hidden">
            <div className="p-3 border-b bg-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <FileText className="h-4 w-4 text-blue-600" />
                Contrat PDF original
              </div>
              <a href={`${API}/files/${contract.file_asset_id}`} target="_blank" rel="noreferrer"
                 className="text-xs text-blue-600 hover:underline"
                 data-testid="contract-pdf-open-new-tab">
                Ouvrir en plein écran
              </a>
            </div>
            <iframe src={`${API}/files/${contract.file_asset_id}#toolbar=1`}
                    title="Contrat PDF" className="w-full h-[600px] border-0"
                    data-testid="contract-pdf-iframe" />
          </CardContent>
        </Card>
      )}

      <ContractBillingBridgePanel contract={contract} />

      {/* Détails du contrat — champs éditables inline */}
      <ContractDetailsSection contract={contract} canWrite={canWrite}
                              onSaved={load} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500">Montant HT</div>
          <div className="mt-1 text-xl font-semibold font-mono">
            {fmtEUR(Number(contract.amount_ht) || (Number(contract.abonnement_gestion_interne) || 0) + (Number(contract.abonnement_finance) || 0))}
          </div>
        </Card>
        <Card className="p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500">Cycle</div>
          <div className="mt-1 text-sm font-medium">{CYCLE_FR[contract.billing_cycle]}</div>
        </Card>
        <Card className="p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500">Début</div>
          <div className="mt-1 text-sm font-mono">{fmtDate(contract.start_date)}</div>
        </Card>
        <Card className="p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500">Fin</div>
          <div className="mt-1 text-sm font-mono">{fmtDate(contract.end_date)}</div>
        </Card>
      </div>

      {/* Lien Véhicule — uniquement contrats fournisseurs (leasing / assurance auto).
          Masqué sur contrats clients (le tenant vend un service, pas de véhicule à lier). */}
      {contract.party_type !== "client" && (
      <Card data-testid="linked-vehicle-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Car className="w-4 h-4 text-slate-500" />
              <h3 className="text-sm font-semibold">Véhicule lié</h3>
            </div>
            {canWrite && (
              linkedVehicle && linkedVehicle.vehicle ? (
                <Button variant="ghost" size="sm" onClick={unlinkVehicle} className="text-red-600 h-7" data-testid="unlink-vehicle-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1" />Délier
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => { setLinkDialogOpen(true); searchVehicles(); }} data-testid="link-vehicle-btn">
                  <Link2 className="w-3.5 h-3.5 mr-1" />Lier un véhicule
                </Button>
              )
            )}
          </div>
          {linkedVehicle && linkedVehicle.vehicle ? (
            <button onClick={() => navigate(`/app/fleet/vehicles/${linkedVehicle.vehicle.id}`)}
              className="mt-3 w-full flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 text-left">
              <Car className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <div className="font-mono font-semibold text-slate-900">{linkedVehicle.vehicle.plate}</div>
                <div className="text-xs text-slate-500">{linkedVehicle.vehicle.brand} {linkedVehicle.vehicle.model}</div>
              </div>
              <Badge variant="outline">{({active:"Actif",in_repair:"En réparation",sold:"Vendu",terminated:"Résilié",reserve:"Réserve"}[linkedVehicle.vehicle.status]) || linkedVehicle.vehicle.status}</Badge>
            </button>
          ) : (
            <p className="mt-2 text-xs text-slate-400">Aucun véhicule lié. Utile pour les contrats d'assurance auto / leasing.</p>
          )}
        </CardContent>
      </Card>
      )}

      {/* GED — Dossiers + documents */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Dossiers & documents</h2>
            {canWrite && (
              <div className="flex gap-2">
                <ZipImportButton contractId={id} folders={contract.folders}
                  onDone={() => { load(); suggestAnalyze(); }} />
                <Button variant="outline" size="sm" onClick={() => setShowNewFolder(true)}
                        data-testid="detail-new-folder-btn">
                  <FolderPlus className="h-3.5 w-3.5 mr-1" /> Nouveau dossier
                </Button>
              </div>
            )}
          </div>
          {(contract.folders || []).length === 0 ? (
            <div className="text-sm text-slate-400 py-4">Aucun dossier. Créez-en un pour organiser les documents.</div>
          ) : (
            <div className="space-y-3">
              {(contract.folders || []).map((f) => {
                const docs = (contract.documents || []).filter((d) => d.folder_id === f.id);
                const isOpen = !!openFolders[f.id];
                return (
                <div key={f.id} className="border border-slate-200 rounded-lg p-3"
                      data-testid={`folder-${f.id}`}>
                  <div className="flex items-center justify-between">
                    <button type="button" onClick={() => toggleFolder(f.id)}
                            className="flex items-center gap-1.5 font-medium text-sm text-left hover:text-blue-700"
                            data-testid={`folder-toggle-${f.id}`}>
                      <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                      📁 {f.name}
                      <span className="ml-1 text-[11px] font-normal px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600"
                            data-testid={`folder-count-${f.id}`}>
                        {docs.length} fichier{docs.length > 1 ? "s" : ""}
                      </span>
                      {f.is_default && (
                        <span className="ml-1 text-[9px] px-1.5 py-0.5 bg-slate-100 rounded text-slate-500">défaut</span>
                      )}
                    </button>
                    {canWrite && (
                      <div className="flex gap-1 items-center">
                        <label className="cursor-pointer">
                          <input id={`contract-folder-upload-${f.id}`} name={`contract-folder-upload-${f.id}`}
                                  type="file" multiple className="hidden" accept={CONTRACT_DOC_ACCEPT}
                                  data-testid={`contract-folder-upload-${f.id}`}
                                  onChange={(e) => { uploadDocs(f.id, e.target.files); e.target.value = ""; }} />
                          <span className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                            <Upload className="h-3 w-3" /> Ajouter
                          </span>
                        </label>
                        <button onClick={() => renameFolder(f.id, f.name)}
                                 className="text-xs text-slate-500 hover:text-blue-600 ml-2"
                                 data-testid={`rename-folder-${f.id}`}
                                 title="Renommer">
                          <Pencil className="h-3 w-3 inline" />
                        </button>
                        <button onClick={() => deleteFolder(f.id)}
                                 className="text-xs text-red-500 hover:underline ml-1">
                          <Trash2 className="h-3 w-3 inline" />
                        </button>
                      </div>
                    )}
                  </div>
                  {isOpen && (
                  <div className="space-y-1 mt-2" data-testid={`folder-docs-${f.id}`}>
                    {docs.length === 0 && <div className="pl-6 text-xs text-slate-400">Dossier vide</div>}
                    {docs.map((d) => (
                      <div key={d.id} className="flex items-center gap-2 text-xs text-slate-600 pl-4">
                        <FileText className="h-3 w-3 text-slate-400" />
                        <a href={`${API}/files/${d.file_asset_id}`} target="_blank" rel="noreferrer"
                            className="flex-1 hover:text-blue-600 hover:underline">
                          {d.name}
                        </a>
                        <span className="text-[10px] text-slate-400">{fmtDate(d.uploaded_at)}</span>
                        {canWrite && (
                          <>
                            <button onClick={() => moveDoc(d.id)}
                                    className="text-slate-500 hover:text-blue-600"
                                    title="Déplacer"
                                    data-testid={`move-doc-${d.id}`}>
                              <MoveRight className="h-3 w-3" />
                            </button>
                            <button onClick={() => detachDoc(d.id)}
                                     className="text-red-500 hover:underline">×</button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historique reconductions */}
      {(contract.renewals || []).length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h2 className="text-lg font-semibold mb-3">Historique reconductions</h2>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Ancienne fin</TableHead>
                <TableHead>Nouvelle fin</TableHead>
                <TableHead>Note</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {contract.renewals.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-xs">{fmtDate(r.renewed_at)}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{r.type}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{fmtDate(r.previous_end)}</TableCell>
                    <TableCell className="font-mono text-xs">{fmtDate(r.new_end)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.note || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <Dialog open={showReconduct} onOpenChange={setShowReconduct}>
        <DialogContent>
          <DialogHeader><DialogTitle>Marquer comme reconduit</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div><Label className="text-xs">Note (optionnel)</Label>
            <Textarea value={reconductNote} onChange={(e) => setReconductNote(e.target.value)}
                       className="mt-1" placeholder="Contexte de la reconduction…" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReconduct(false)}>Annuler</Button>
            <Button onClick={markReconduct} data-testid="reconduct-confirm-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPreavis} onOpenChange={setShowPreavis}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirmer l'envoi du préavis</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div><Label className="text-xs">Note (optionnel)</Label>
            <Textarea value={preavisNote} onChange={(e) => setPreavisNote(e.target.value)}
                       className="mt-1" placeholder="LRAR n°… envoyée le…" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreavis(false)}>Annuler</Button>
            <Button onClick={markPreavis} data-testid="preavis-confirm-btn">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent>
          <DialogHeader><DialogTitle>Programmer la résiliation</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Date effective *</Label>
              <div className="mt-1"><DatePickerFR value={cancelDate} onChange={setCancelDate} /></div></div>
            <div><Label className="text-xs">Motif</Label>
              <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                         className="mt-1" placeholder="Motif de la résiliation" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancel(false)}>Annuler</Button>
            <Button onClick={scheduleCancel}
                     className="bg-red-600 hover:bg-red-700 text-white"
                     data-testid="cancel-confirm-btn">
              Programmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Résiliation en cours — encart proéminent */}
      {resiliationDocs.resiliation_status === "en_cours" && resiliationDocs.resiliation_data && (
        <Card className="border-red-200 bg-red-50/30" data-testid="resiliation-en-cours-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold text-red-900 flex items-center gap-2">
                  <FileSignature className="h-5 w-5" /> Résiliation en cours
                </h2>
                <p className="text-xs text-red-700 mt-1">
                  Demandée le {fmtDate(resiliationDocs.resiliation_data.date_demande)}
                  {resiliationDocs.resiliation_data.reference_ar
                    && ` — AR n° ${resiliationDocs.resiliation_data.reference_ar}`}
                </p>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-red-500">Date d'effet</div>
                <div className="text-lg font-mono font-semibold text-red-900">
                  {fmtDate(resiliationDocs.resiliation_data.date_effet)}
                </div>
                <div className="text-[10px] text-red-500 mt-0.5">
                  Préavis {resiliationDocs.resiliation_data.duree_preavis_mois} mois
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs font-medium text-red-800 mb-2">Documents ({resiliationDocs.documents.length})</div>
              {resiliationDocs.documents.map((d) => (
                <div key={d.id} className="flex items-center gap-2 text-xs bg-white p-2 rounded border border-red-200"
                     data-testid={`resiliation-doc-${d.id}`}>
                  <FileText className="h-3 w-3 text-slate-400" />
                  <a href={`${API}/contracts/${id}/demande-resiliation/document/${d.id}`}
                     target="_blank" rel="noreferrer"
                     className="flex-1 truncate hover:text-blue-600 hover:underline">
                    {d.name}
                  </a>
                  {d.category === "lettre_acceptation" && (
                    <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                      Lettre acceptation
                    </Badge>
                  )}
                  {d.category === "resiliation_piece" && (
                    <Badge variant="outline" className="text-[9px]">Pièce</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historique résiliations annulées */}
      {resiliationDocs.resiliation_history?.length > 0 && (
        <Card><CardContent className="p-5">
          <h2 className="text-sm font-semibold mb-3 text-slate-700">
            Historique résiliations annulées ({resiliationDocs.resiliation_history.length})
          </h2>
          <div className="space-y-1 text-xs">
            {resiliationDocs.resiliation_history.map((h, i) => (
              <div key={i} className="flex items-center gap-2 text-slate-500 py-1">
                <Undo2 className="h-3 w-3" />
                Annulée le <span className="font-mono">{fmtDate(h.annulee_le)}</span>
                <span>par {h.annulee_par_email || "—"}</span>
                <span className="text-slate-400">— demandée le {fmtDate(h.date_demande)}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      <ResiliationDialog
        open={showResiliation}
        onOpenChange={setShowResiliation}
        contract={contract}
        onDone={load}
      />

      <TerminationSimulatorDialog
        open={showTermSim}
        onOpenChange={setShowTermSim}
        contract={contract}
      />

      <LetterAssistantDialog
        open={showLetterAssist}
        onOpenChange={setShowLetterAssist}
        contract={contract}
      />

      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau dossier</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div><Label className="text-xs">Nom</Label>
            <Input value={folderName} onChange={(e) => setFolderName(e.target.value)}
                    className="mt-1" placeholder="Ex : Avenants, LRAR…"
                    data-testid="folder-name-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewFolder(false)}>Annuler</Button>
            <Button onClick={createFolder} data-testid="folder-create-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent data-testid="link-vehicle-dialog">
          <DialogHeader><DialogTitle>Lier un véhicule</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input value={vehicleSearch} onChange={(e) => setVehicleSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchVehicles()}
                placeholder="Rechercher immat, marque, modèle..." data-testid="veh-search-input" />
              <Button variant="outline" onClick={searchVehicles} data-testid="veh-search-btn">
                <Search className="w-4 h-4" />
              </Button>
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1">
              {vehicleCandidates.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Aucun véhicule.</p>
              ) : vehicleCandidates.map((v) => (
                <button key={v.id} onClick={() => linkVehicle(v.id)}
                  className="w-full flex items-center gap-3 p-2 rounded hover:bg-slate-50 text-left"
                  data-testid={`veh-candidate-${v.id}`}>
                  <Car className="w-4 h-4 text-slate-400" />
                  <div className="flex-1">
                    <div className="font-mono font-medium text-sm">{v.plate}</div>
                    <div className="text-xs text-slate-500">{v.brand} {v.model}</div>
                  </div>
                  <Badge variant="outline">{({active:"Actif",in_repair:"En réparation",sold:"Vendu",terminated:"Résilié",reserve:"Réserve"}[v.status]) || v.status}</Badge>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================================================
 * ContractDetailsSection — Fiche complète éditable inline.
 * Regroupe Infos générales + Montants + Financement/Leasing +
 * Banque. Chaque champ = "—" si vide, cliquable pour édition,
 * sauvegarde via PATCH /contracts/{id}.
 * ============================================================ */
function ContractDetailsSection({ contract, canWrite, onSaved }) {
  const cx = (...c) => c.filter(Boolean).join(" ");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const start = (key, current) => {
    if (!canWrite) return;
    setEditing(key);
    setDraft(current == null || current === "" ? "" : String(current));
  };
  const cancel = () => { setEditing(null); setDraft(""); };
  const save = async (key, type) => {
    let value = draft;
    if (type === "number") {
      value = draft === "" ? null : parseFloat(draft);
      if (Number.isNaN(value)) { toast.error("Nombre invalide"); return; }
    } else if (type === "date") {
      value = draft || null;
    } else {
      value = (draft || "").trim() || null;
    }
    setSaving(true);
    try {
      await api.patch(`/contracts/${contract.id}`, { [key]: value });
      toast.success("Champ mis à jour");
      setEditing(null); setDraft("");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const F = ({ label, k, type = "text", format }) => {
    const raw = contract[k];
    const isEmpty = raw == null || raw === "";
    const display = format
      ? format(raw)
      : (isEmpty ? null
         : type === "number" ? fmtEUR(raw)
         : type === "date" ? fmtDate(raw)
         : String(raw));
    const isEditing = editing === k;
    return (
      <div className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0"
           data-testid={`detail-field-${k}`}>
        <div className="text-xs text-slate-500 pt-1 min-w-[140px]">{label}</div>
        <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
          {isEditing ? (
            <>
              <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                     type={type === "number" ? "number" : type === "date" ? "date" : "text"}
                     step={type === "number" ? "0.01" : undefined}
                     className="h-8 text-sm max-w-[220px]" autoFocus
                     onKeyDown={(e) => {
                       if (e.key === "Enter") save(k, type);
                       if (e.key === "Escape") cancel();
                     }}
                     data-testid={`detail-input-${k}`} />
              <Button size="sm" variant="ghost" onClick={() => save(k, type)}
                      disabled={saving}
                      className="h-8 px-2 text-emerald-600 hover:text-emerald-700"
                      data-testid={`detail-save-${k}`}>
                <CheckCircle2 className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={cancel}
                      className="h-8 px-2 text-slate-500"
                      data-testid={`detail-cancel-${k}`}>
                ✕
              </Button>
            </>
          ) : (
            <>
              <span className={cx(
                "text-sm text-right truncate",
                display == null ? "text-slate-400 italic" : "text-slate-900 font-medium"
              )}>
                {display == null ? "—" : display}
              </span>
              {canWrite && (
                <button onClick={() => start(k, raw)}
                        className="text-slate-400 hover:text-blue-600 shrink-0"
                        title="Modifier"
                        data-testid={`detail-edit-${k}`}>
                  <Pencil className="h-3 w-3" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Total abonnement = interne + finance (fallback amount_ht si absents)
  const totalAbo = (Number(contract.abonnement_gestion_interne) || 0)
                 + (Number(contract.abonnement_finance) || 0);
  const totalDisplay = totalAbo > 0 ? totalAbo : contract.amount_ht;

  return (
    <Card data-testid="contract-details-section">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Détails du contrat</h2>
          {canWrite && (
            <span className="text-[11px] text-slate-400">
              Cliquez sur ✎ pour modifier chaque champ
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2">
          {/* Colonne 1 — Infos générales */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
              Informations générales
            </div>
            <F label="Type" k="contract_type"
               format={(v) => v ? (CONTRACT_TYPE_FR[v] || v) : null} />
            <F label="Catégorie de service" k="service_category" />
            <F label="Produit" k="produit" />
            <F label="Durée (mois)" k="duree_mois" type="number"
               format={(v) => (v == null || v === "") ? null : `${v} mois`} />
            <F label="Date signature" k="date_signature" type="date" />
            <F label="Date installation" k="date_installation" type="date" />
            <F label="Date fin" k="end_date" type="date" />
            <F label="Vendeur" k="vendeur" />
            <F label="Revendeur" k="revendeur" />
            <F label="Cycle" k="billing_cycle"
               format={(v) => v ? (CYCLE_FR[v] || v) : null} />
          </div>

          {/* Colonne 2 — Montants & abonnements */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
              Montants & abonnements
            </div>
            <F label="Abonnement gestion interne" k="abonnement_gestion_interne" type="number" />
            <F label="Abonnement finance" k="abonnement_finance" type="number" />
            <div className="flex items-start justify-between gap-3 py-2 border-b border-slate-100"
                 data-testid="detail-total-abonnement">
              <div className="text-xs text-slate-500 pt-1 min-w-[140px]">Montant total</div>
              <span className={cx(
                "text-sm text-right font-semibold",
                totalDisplay ? "text-blue-700" : "text-slate-400 italic font-normal"
              )}>
                {totalDisplay ? fmtEUR(totalDisplay) : "—"}
              </span>
            </div>
            <F label="Frais installation" k="frais_installation" type="number" />
            <F label="Coût installation" k="cout_installation" type="number" />
            <F label="Location serveur" k="location_serveur" type="number" />
            <F label="Vente matériel" k="vente_materiel" type="number" />
            <F label="Commission" k="commission" type="number" />
            <F label="TVA %" k="vat_rate" type="number"
               format={(v) => (v == null || v === "") ? null : `${v} %`} />
          </div>

          {/* Colonne 3 — Financement / Leasing + Banque */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
              Financement / Leasing
            </div>
            <F label="Leaser" k="leaser" />
            <F label="N° financement" k="numero_financement" />
            <F label="CA Leaser" k="ca_leaser" type="number" />
            <F label="Rachat" k="rachat" type="number" />

            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2 mt-5">
              Coordonnées bancaires
            </div>
            <F label="Banque" k="banque" />
            <F label="IBAN" k="iban" />
            <F label="BIC" k="bic" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

